import { useEffect, useState } from "react";
import { X, Clock, Upload, Trash2, FileCheck, RotateCcw, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Round 22 close-out · History drawer.
 *
 * Admin-only, read-only. Opens from the pay-run detail "History"
 * button. Lists biometric audit entries (submit/apply/discard/
 * replace), the pay-run's reopen acknowledgement against a lodged
 * S2 if any, and the S2 lodgement/supersede events for the
 * period's PPE month. Newest first.
 *
 * Feed shape: GET /api/pay-periods/{pid}/history →
 *   {pid, ppe_month, entries: [{kind, action, at, by, reason,
 *                                 by_role, label}]}
 */
const ICONS = {
    biometric_applied: Upload,
    biometric_discarded: Trash2,
    biometric_replaced: RotateCcw,
    s2_lodgement_lodged: FileCheck,
    s2_lodgement_superseded: RotateCcw,
    reopen_ack_reopened_against_lodged_s2: RotateCcw,
    early_start_override_early_start_authorised: ShieldCheck,
    early_start_override_early_start_revoked: RotateCcw,
};
const COLOURS = {
    biometric_applied: "#0A8A3C",
    biometric_discarded: "#8A1F1F",
    biometric_replaced: "#8A6D00",
    s2_lodgement_lodged: "#0A8A3C",
    s2_lodgement_superseded: "#8A6D00",
    reopen_ack_reopened_against_lodged_s2: "#8A1F1F",
    early_start_override_early_start_authorised: "#0A8A3C",
    early_start_override_early_start_revoked: "#8A6D00",
};

export default function HistoryDrawer({ pid, open, onClose }) {
    const [entries, setEntries] = useState(null);

    useEffect(() => {
        if (!open || !pid) return;
        setEntries(null);
        api.get(`/pay-periods/${pid}/history`).then((r) => {
            setEntries(r.data?.entries || []);
        }).catch((ex) => {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not load history");
            setEntries([]);
        });
    }, [pid, open]);

    if (!open) return null;

    return (
        <>
            <button
                data-testid="history-drawer-scrim"
                onClick={onClose}
                aria-label="Close history"
                className="fixed inset-0 z-40 bg-black/40"
            />
            <aside
                data-testid="history-drawer"
                className="fixed top-0 right-0 z-50 h-full w-full max-w-[480px] bg-white flex flex-col shadow-2xl"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
                    <div className="flex items-center gap-2">
                        <Clock size={18} className="text-[color:var(--kp-charcoal)]" />
                        <div className="font-display font-bold text-base text-[color:var(--kp-charcoal)]">History</div>
                    </div>
                    <button
                        data-testid="history-drawer-close"
                        onClick={onClose}
                        className="p-1 text-neutral-500 hover:text-[color:var(--kp-charcoal)]"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {entries === null && (
                        <div className="text-sm text-neutral-500 font-mono-money">loading…</div>
                    )}
                    {entries !== null && entries.length === 0 && (
                        <div data-testid="history-drawer-empty" className="text-sm text-neutral-500">
                            No audit events on this pay period yet.
                        </div>
                    )}
                    {entries !== null && entries.length > 0 && (
                        <ul className="space-y-3" data-testid="history-drawer-list">
                            {entries.map((e, i) => {
                                const key = `${e.kind}_${e.action}`;
                                const Icon = ICONS[key] || Clock;
                                const colour = COLOURS[key] || "#2A2A2C";
                                return (
                                    <li
                                        key={i}
                                        data-testid={`history-entry-${e.kind}-${e.action}`}
                                        className="border border-neutral-200 rounded-[3px] p-3"
                                    >
                                        <div className="flex items-start gap-2">
                                            <Icon size={14} className="mt-0.5 shrink-0" style={{ color: colour }} />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-display font-semibold text-[13px] text-[color:var(--kp-charcoal)]">
                                                    {e.label}
                                                </div>
                                                <div className="text-[11px] text-neutral-500 mt-1 font-mono-money">
                                                    {e.at || "—"} · {e.by || "unknown"}
                                                    {e.by_role ? ` (${e.by_role})` : ""}
                                                </div>
                                                {e.reason && (
                                                    <div className="text-[12px] text-neutral-600 mt-1 italic">
                                                        "{e.reason}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </aside>
        </>
    );
}
